import { makeAutoObservable, runInAction } from 'mobx';
import type { TradeStore } from './trade-store';
import type { RootStore } from './root-store';
import { TradeImportLogMobxDto } from '../mobx/dtos/trade/trade-import-log-mobx-dto';

export class TradeRevertDialogStore {
    rootStore: RootStore;

    tradeStore: TradeStore;

    dialogOpened = false;

    loading = false;

    limit = Number(process.env.NEXT_PUBLIC_PAGE_LIMIT);

    totalCount = 0;

    page = 1;

    tradeImportLogs: TradeImportLogMobxDto[] = [];

    portfolioId: string;

    activeConfirmId: string | null = null;

    constructor(tradeStore: TradeStore) {
        this.rootStore = tradeStore.rootStore;
        this.tradeStore = tradeStore;

        makeAutoObservable(this, {
            rootStore: false,
            tradeStore: false,
        });
    }

    startLoading(): void {
        this.loading = true;
    }

    stopLoading(): void {
        this.loading = false;
    }

    async openDialog(portfolioId: string): Promise<void> {
        this.portfolioId = portfolioId;
        this.dialogOpened = true;
        this.rootStore.alertStore.setModalPlacement();

        await this.fetchImportLogs();
    }

    closeDialog(): void {
        this.dialogOpened = false;
        this.rootStore.alertStore.setDefaultPlacement();
    }

    async fetchImportLogs(): Promise<void> {
        this.startLoading();
        try {
            const result = await this.rootStore.apiClient.importLogTradesApi.tradeImportLogControllerGetList({
                limit: this.limit,
                offset: (this.page - 1) * this.limit,
                portfolioId: this.portfolioId,
            });

            runInAction(() => {
                this.tradeImportLogs = TradeImportLogMobxDto.createFromArray(result.items);
                this.totalCount = result.total;
            });
        } catch (e) {
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.stopLoading();
        }
    }

    async revert(id: string): Promise<void> {
        this.startLoading();
        try {
            await this.rootStore.apiClient.importLogTradesApi.tradeImportLogControllerRevert({
                id,
            });

            await this.tradeStore.recalculateUnprocessedTrades([this.portfolioId]);
            await this.fetchImportLogs();
            await this.tradeStore.fetchTradeList();

            this.closeDialog();
        } catch (e) {
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.stopLoading();
        }
    }

    async setPage(page: number): Promise<void> {
        this.page = page;
        await this.fetchImportLogs();
    }

    setPageLimit(limit: number | string | null): void {
        if (limit && !Number.isNaN(limit)) {
            this.limit = +limit;
            this.setPage(1);
        }
    }

    get pagesCount(): number {
        return Math.ceil(this.totalCount / this.limit) || 0;
    }

    setActiveConfirmId(id: string | null) {
        this.activeConfirmId = id;
    }
}
